// Per-user view preferences (theme, density, help/tooltip level) kept in localStorage.
import { useCallback, useMemo, useSyncExternalStore } from "react";
import { createStore } from "./store";

export type ThemePref = "system" | "light" | "dark";
export type Density = "comfortable" | "compact";
export type HelpLevel = "guided" | "standard" | "off";

export const HELP_LEVEL_OPTIONS: readonly {
  value: HelpLevel;
  label: string;
  shortLabel: string;
  description: string;
}[] = [
  {
    value: "guided",
    label: "Guided (Speed-Learn)",
    shortLabel: "Guided",
    description: "Rich tooltips with Cliff-Notes step hints and a contextual page guide banner.",
  },
  {
    value: "standard",
    label: "Standard tooltips",
    shortLabel: "Standard",
    description: "Concise hover and focus tooltips without extra page banners.",
  },
  {
    value: "off",
    label: "Minimal (Tooltips off)",
    shortLabel: "Off",
    description: "Hides hover tooltips for experienced power users.",
  },
] as const;

function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, v: string) {
  try {
    localStorage.setItem(key, v);
  } catch {
    /* private mode */
  }
}

export const themeStore = createStore<ThemePref>(read("ls.theme", ["system", "light", "dark"] as const, "system"));
export const densityStore = createStore<Density>(read("ls.density", ["comfortable", "compact"] as const, "comfortable"));
export const helpLevelStore = createStore<HelpLevel>(read("ls.helpLevel", ["guided", "standard", "off"] as const, "standard"));

export function setTheme(t: ThemePref) {
  themeStore.set(t);
  write("ls.theme", t);
  if (t === "system") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = t;
}

export function setDensity(d: Density) {
  densityStore.set(d);
  write("ls.density", d);
  if (d === "compact") document.documentElement.dataset.density = "compact";
  else delete document.documentElement.dataset.density;
}

export function setHelpLevel(level: HelpLevel) {
  helpLevelStore.set(level);
  write("ls.helpLevel", level);
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.dataset.helpLevel = level;
  }
}

const mq = typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)") : null;

function subscribeMq(fn: () => void) {
  mq?.addEventListener("change", fn);
  return () => mq?.removeEventListener("change", fn);
}

export function useIsDark(): boolean {
  const pref = themeStore.use();
  const sys = useSyncExternalStore(subscribeMq, () => !!mq?.matches, () => false);
  return pref === "dark" || (pref === "system" && sys);
}

export function useMediaQuery(query: string): boolean {
  const m = useMemo(() => (typeof window !== "undefined" && typeof window.matchMedia === "function" ? window.matchMedia(query) : null), [query]);
  const subscribe = useCallback(
    (fn: () => void) => {
      m?.addEventListener("change", fn);
      return () => m?.removeEventListener("change", fn);
    },
    [m],
  );
  return useSyncExternalStore(subscribe, () => !!m?.matches, () => false);
}
